"use client";

import { useMemo, useRef } from "react";
import styles from "./HeroSection.module.css";

interface DateSelectorProps {
  value: string;
  onChange: (val: string) => void;
}

export default function DateSelector({ value, onChange }: DateSelectorProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const todayISO = useMemo(() => {
    const d = new Date();
    // YYYY-MM-DD
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  }, []);

  const handleClickAnywhere = () => {
    if (!inputRef.current) return;
    try {
      // Works in Chromium; falls back to focus for Safari/Firefox
      inputRef.current.showPicker?.();
    } catch {
      inputRef.current.focus();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Select date of birth";
    const date = new Date(`${dateString}T00:00:00`);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className={styles.dateWrapper}>
      <div className={styles.dateInputWrapper} onClick={handleClickAnywhere}>
        {/* Calendar icon */}
        <span className={styles.dateIcon} aria-hidden="true">
          {/* SVG for calendar */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M8 3v4M16 3v4M3 9h18M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5A2 2 0 0 0 3 7v12a2 2 0 0 0 2 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>

        {/* Displayed value */}
        <span className={styles.dateDisplayText} data-filled={!!value}>
          {formatDate(value)}
        </span>

        {/* Hidden native input (captures clicks & opens picker) */}
        <input
          ref={inputRef}
          type="date"
          value={value}
          max={todayISO}
          onChange={(e) => onChange(e.target.value)}
          className={styles.dateInputNative}
          aria-label="Select date of birth"
        />

        {/* Chevron */}
        <span className={styles.dateChevron} aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </div>
  );
}
